import React, { useState, useEffect } from 'react';
import { invoiceService, caseService, userService, searchClientsAsync } from '../../services/api';
import { X, Plus, Trash2, Clock, DollarSign, Receipt } from 'lucide-react';
import useModal from '../../hooks/useModal';
import Notification from '../common/Notification';
import useNotification from '../../hooks/useNotification';
import SearchableSelect from '../common/SearchableSelect';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';

const CreateInvoiceModal = ({ onClose, onSuccess, initialCaseId, initialClientId, initialClientName }) => {
  const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'tasks'
  const [loading, setLoading] = useState(false);
  const [attorneys, setAttorneys] = useState([]);
  const [manualClientCases, setManualClientCases] = useState([]);
  const [taskClientCases, setTaskClientCases] = useState([]);

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Manual invoice form
  const [invoiceData, setInvoiceData] = useState({
    case_id: '',
    client_id: initialClientId || '',
    billing_attorney_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    tax_rate: 0,
    line_items: [
      {
        description: '',
        date: new Date().toISOString().split('T')[0],
        item_type: 'time',
        attorney_id: '',
        hours: '',
        rate: '',
        amount: '',
        billable: true
      }
    ]
  });

  // Task-based invoice form
  const [taskInvoiceData, setTaskInvoiceData] = useState({
    case_id: '',
    client_id: initialClientId || '',
    billing_attorney_id: '',
    due_date: '',
    description: '',
    task_ids: [],
    project_ids: [],
    attorney_ids: []
  });

  const [unbilledTasks, setUnbilledTasks] = useState([]);
  const [unbilledTime, setUnbilledTime] = useState([]);
  const [unbilledExpenses, setUnbilledExpenses] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [selectedTimeEntries, setSelectedTimeEntries] = useState(new Set());
  const [selectedExpenses, setSelectedExpenses] = useState(new Set());
  const { notification, showError, showSuccess, showWarning, hideNotification } = useNotification();

  useEffect(() => {
    fetchData();
  }, []);

  // Pre-fill case and client when initialCaseId is provided
  useEffect(() => {
    if (!initialCaseId) return;
    caseService.getCase(initialCaseId).then(res => {
      const matchedCase = res.data;
      if (matchedCase) {
        setInvoiceData(prev => ({
          ...prev,
          case_id: matchedCase.id,
          client_id: matchedCase.client_id || '',
        }));
        setTaskInvoiceData(prev => ({
          ...prev,
          case_id: matchedCase.id,
          client_id: matchedCase.client_id || '',
        }));
      }
    }).catch(err => console.error('Error fetching initial case:', err));
  }, [initialCaseId]);

  useEffect(() => {
    if (taskInvoiceData.case_id) {
      fetchUnbilledItems(taskInvoiceData.case_id);
    }
  }, [taskInvoiceData.case_id]);

  // Load cases for manual tab client
  useEffect(() => {
    if (!invoiceData.client_id) { setManualClientCases([]); return; }
    caseService.getCasesByClient(invoiceData.client_id).then(res => {
      setManualClientCases(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setManualClientCases([]));
  }, [invoiceData.client_id]);

  // Load cases for task tab client
  useEffect(() => {
    if (!taskInvoiceData.client_id) { setTaskClientCases([]); return; }
    caseService.getCasesByClient(taskInvoiceData.client_id).then(res => {
      setTaskClientCases(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setTaskClientCases([]));
  }, [taskInvoiceData.client_id]);

  const fetchData = async () => {
    try {
      const attorneysResponse = await userService.getUsers();
      const activeAttorneys = (attorneysResponse.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
      setAttorneys(activeAttorneys);
    } catch (error) {
      console.error('Could not load attorneys:', error);
      setAttorneys([]);
    }
  };

  const fetchUnbilledItems = async (caseId) => {
    try {
      const [tasksRes, timeRes, expensesRes] = await Promise.all([
        invoiceService.getUnbilledTasks(caseId),
        invoiceService.getUnbilledTime(caseId),
        invoiceService.getUnbilledExpenses(caseId)
      ]);
      setUnbilledTasks(tasksRes.data || []);
      // Filter out time entries already linked to unbilled tasks
      const taskIds = new Set((tasksRes.data || []).map(t => String(t.id)));
      const standaloneTime = (timeRes.data || []).filter(t => !t.task_id || !taskIds.has(String(t.task_id)));
      setUnbilledTime(standaloneTime);
      setUnbilledExpenses(expensesRes.data || []);
    } catch (error) {
      console.error('Error fetching unbilled items:', error);
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();

    if (!invoiceData.case_id || !invoiceData.client_id || !invoiceData.billing_attorney_id) {
      showWarning('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Use provided invoice_date or default to today
      const invoiceDate = invoiceData.invoice_date ?
        new Date(invoiceData.invoice_date).toISOString() :
        new Date().toISOString();

      // Calculate due date if not set (default 30 days from now)
      const dueDate = invoiceData.due_date ?
        new Date(invoiceData.due_date).toISOString() :
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const submitData = {
        ...invoiceData,
        invoice_date: invoiceDate,
        due_date: dueDate,
        tax_rate: parseFloat(invoiceData.tax_rate) || 0,
        line_items: invoiceData.line_items.map(item => ({
          ...item,
          attorney_id: item.attorney_id || null,
          hours: item.hours ? parseFloat(item.hours) : null,
          rate: item.rate ? parseFloat(item.rate) : null,
          quantity: item.quantity ? parseFloat(item.quantity) : null,
          unit_price: item.unit_price ? parseFloat(item.unit_price) : null,
          amount: parseFloat(item.amount) || 0
        }))
      };

      const response = await invoiceService.createInvoice(submitData);
      onSuccess(response.data);
    } catch (error) {
      console.error('Error creating invoice:', error);
      showError('Error creating invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();

    if (!taskInvoiceData.case_id || !taskInvoiceData.client_id || !taskInvoiceData.billing_attorney_id) {
      showWarning('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      // Calculate due date if not set (default 30 days from now)
      const dueDate = taskInvoiceData.due_date ?
        new Date(taskInvoiceData.due_date).toISOString() :
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      // Build line items from selected tasks, time entries, and expenses
      const lineItems = [];

      // Add task-based line items
      unbilledTasks.filter(t => selectedTasks.has(t.id)).forEach(task => {
        lineItems.push({
          description: task.title,
          date: task.completed_at ? new Date(task.completed_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          item_type: 'time',
          task_id: task.id,
          attorney_id: task.assigned_to || null,
          hours: task.actual_hours,
          rate: task.billing_rate || 0,
          amount: task.estimated_amount || 0,
          billable: true,
        });
      });

      // Add time entry line items
      unbilledTime.filter(t => selectedTimeEntries.has(t.id)).forEach(entry => {
        lineItems.push({
          description: entry.description || 'Time entry',
          date: entry.logged_at ? new Date(entry.logged_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          item_type: 'time',
          time_entry_id: entry.id,
          attorney_id: entry.user_id || null,
          hours: entry.hours,
          rate: entry.rate || 0,
          amount: entry.amount || 0,
          billable: true,
        });
      });

      // Add expense line items
      unbilledExpenses.filter(e => selectedExpenses.has(e.id)).forEach(expense => {
        lineItems.push({
          description: expense.description + (expense.category ? ` (${expense.category})` : ''),
          date: expense.date,
          item_type: 'expense',
          expense_id: expense.id,
          quantity: expense.quantity || 1,
          unit_price: expense.amount / (expense.quantity || 1),
          amount: expense.amount,
          billable: true,
        });
      });

      const submitData = {
        case_id: taskInvoiceData.case_id,
        client_id: taskInvoiceData.client_id,
        billing_attorney_id: taskInvoiceData.billing_attorney_id,
        invoice_date: new Date().toISOString(),
        due_date: dueDate,
        description: taskInvoiceData.description,
        line_items: lineItems,
      };

      const response = await invoiceService.createInvoice(submitData);
      onSuccess(response.data);
    } catch (error) {
      console.error('Error creating invoice from tasks:', error);
      showError('Error creating invoice from tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          description: '',
          date: new Date().toISOString().split('T')[0],
          item_type: 'time',
          attorney_id: prev.billing_attorney_id || '',
          hours: '',
          rate: '',
          amount: '',
          billable: true
        }
      ]
    }));
  };

  const removeLineItem = (index) => {
    setInvoiceData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Rate update prompt state
  const [ratePrompt, setRatePrompt] = useState(null); // { attorneyId, attorneyName, newRate }

  const updateLineItem = (index, field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));

    // Auto-populate rate when attorney is selected
    if (field === 'attorney_id' && value) {
      const attorney = attorneys.find(a => a.id === value);
      if (attorney?.hourly_rate) {
        updateLineItem(index, 'rate', parseFloat(attorney.hourly_rate).toFixed(2));
      }
    }

    // Auto-calculate amount
    if (field === 'hours' || field === 'rate') {
      const item = invoiceData.line_items[index];
      const hours = field === 'hours' ? parseFloat(value) || 0 : parseFloat(item.hours) || 0;
      const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(item.rate) || 0;

      if (hours && rate) {
        updateLineItem(index, 'amount', (hours * rate).toFixed(2));
      }

      // If rate was manually changed, check if it differs from attorney's default
      if (field === 'rate') {
        const attorneyId = item.attorney_id;
        if (attorneyId) {
          const attorney = attorneys.find(a => a.id === attorneyId);
          const defaultRate = attorney?.hourly_rate ? parseFloat(attorney.hourly_rate) : null;
          const newRate = parseFloat(value) || 0;
          if (defaultRate !== null && newRate > 0 && newRate !== defaultRate) {
            setRatePrompt({
              attorneyId,
              attorneyName: `${attorney.first_name} ${attorney.last_name}`,
              newRate
            });
          }
        }
      }
    }
    if (field === 'quantity' || field === 'unit_price') {
      const item = invoiceData.line_items[index];
      const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(item.quantity) || 0;
      const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(item.unit_price) || 0;

      if (qty && price) {
        updateLineItem(index, 'amount', (qty * price).toFixed(2));
      }
    }
  };

  const handleUpdateDefaultRate = async () => {
    if (!ratePrompt) return;
    try {
      await userService.updateUser(ratePrompt.attorneyId, { hourly_rate: ratePrompt.newRate });
      // Update local attorneys list
      setAttorneys(prev => prev.map(a =>
        a.id === ratePrompt.attorneyId ? { ...a, hourly_rate: ratePrompt.newRate } : a
      ));
      showSuccess(`Default rate updated to $${ratePrompt.newRate.toFixed(2)}/hr for ${ratePrompt.attorneyName}`);
    } catch {
      showError('Failed to update default rate');
    }
    setRatePrompt(null);
  };

  const toggleTaskSelection = (taskId) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const toggleTimeEntrySelection = (id) => {
    setSelectedTimeEntries(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpenseSelection = (id) => {
    setSelectedExpenses(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const calculateTaskTotal = () => {
    const taskTotal = unbilledTasks
      .filter(task => selectedTasks.has(task.id))
      .reduce((total, task) => total + (task.estimated_amount || 0), 0);
    const timeTotal = unbilledTime
      .filter(entry => selectedTimeEntries.has(entry.id))
      .reduce((total, entry) => total + (entry.amount || 0), 0);
    const expenseTotal = unbilledExpenses
      .filter(expense => selectedExpenses.has(expense.id))
      .reduce((total, expense) => total + (expense.amount || 0), 0);
    return taskTotal + timeTotal + expenseTotal;
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create New Invoice</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('manual')}
              className={`py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'manual'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Manual Invoice
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`py-3 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'tasks'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              From Tasks
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {activeTab === 'manual' ? (
            <form onSubmit={handleManualSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client *
                  </label>
                  <AsyncSearchableSelect
                    value={invoiceData.client_id}
                    onChange={(val) => {
                      setInvoiceData(prev => ({
                        ...prev,
                        client_id: val,
                        case_id: ''
                      }));
                    }}
                    onSearch={searchClientsAsync}
                    emptyOption="Select Client"
                    selectedLabel={initialClientId ? initialClientName : undefined}
                    required
                    disabled={!!initialClientId}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Case *
                  </label>
                  <SearchableSelect
                    value={invoiceData.case_id}
                    onChange={(val) => setInvoiceData(prev => ({ ...prev, case_id: val }))}
                    options={manualClientCases.map(c => ({
                      value: c.id,
                      label: `${c.case_number} - ${c.title}`
                    }))}
                    emptyOption={!invoiceData.client_id ? 'Select Client First' : 'Select Case'}
                    required
                    disabled={!invoiceData.client_id}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Attorney *
                  </label>
                  <SearchableSelect
                    value={invoiceData.billing_attorney_id}
                    onChange={(val) => setInvoiceData(prev => ({ ...prev, billing_attorney_id: val }))}
                    options={(attorneys || []).map(attorney => ({
                      value: attorney.id,
                      label: `${attorney.first_name} ${attorney.last_name}`
                    }))}
                    emptyOption="Select Attorney"
                    required
                  />
                </div>
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Date
                  </label>
                  <input
                    type="date"
                    value={invoiceData.invoice_date}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, invoice_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={invoiceData.due_date}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Rate (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={invoiceData.tax_rate}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, tax_rate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={invoiceData.description}
                  onChange={(e) => setInvoiceData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Invoice description..."
                />
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="bg-yellow-500 text-gray-900 px-3 py-1 rounded text-sm hover:bg-yellow-600 flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </button>
                </div>

                {invoiceData.line_items.map((item, index) => (
                  <div key={index} className={`border rounded-lg p-4 mb-4 ${item.billable === false ? 'border-gray-300 bg-gray-50' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          placeholder={item.item_type === 'expense' ? 'Expense description...' : 'Description of work...'}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={item.item_type}
                          onChange={(e) => updateLineItem(index, 'item_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                        >
                          <option value="time">Time</option>
                          <option value="expense">Expense</option>
                          <option value="flat_fee">Flat Fee</option>
                        </select>
                      </div>

                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={item.billable !== false}
                            onChange={(e) => updateLineItem(index, 'billable', e.target.checked)}
                            className="w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Billable</span>
                        </label>
                      </div>
                    </div>

                    {item.item_type === 'time' ? (
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date
                          </label>
                          <input
                            type="date"
                            value={item.date}
                            onChange={(e) => updateLineItem(index, 'date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Performed By
                          </label>
                          <select
                            value={item.attorney_id || ''}
                            onChange={(e) => updateLineItem(index, 'attorney_id', e.target.value || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          >
                            <option value="">-- Select --</option>
                            {(attorneys || []).map(attorney => (
                              <option key={attorney.id} value={attorney.id}>
                                {attorney.first_name} {attorney.last_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hours
                          </label>
                          <input
                            type="number"
                            step="0.25"
                            value={item.hours}
                            onChange={(e) => updateLineItem(index, 'hours', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rate
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => updateLineItem(index, 'rate', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.amount}
                            onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div className="flex items-end justify-end">
                          {invoiceData.line_items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="mb-2 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date
                          </label>
                          <input
                            type="date"
                            value={item.date}
                            onChange={(e) => updateLineItem(index, 'date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            value={item.quantity || 1}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unit Price
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.unit_price || ''}
                            onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.amount}
                            onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                          />
                        </div>

                        <div className="flex items-end justify-end">
                          {invoiceData.line_items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="mb-2 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </form>
          ) : (
            <form onSubmit={handleTaskSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client *
                  </label>
                  <AsyncSearchableSelect
                    value={taskInvoiceData.client_id}
                    onChange={(val) => {
                      setTaskInvoiceData(prev => ({
                        ...prev,
                        client_id: val,
                        case_id: ''
                      }));
                    }}
                    onSearch={searchClientsAsync}
                    emptyOption="Select Client"
                    selectedLabel={initialClientId ? initialClientName : undefined}
                    required
                    disabled={!!initialClientId}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Case *
                  </label>
                  <SearchableSelect
                    value={taskInvoiceData.case_id}
                    onChange={(val) => setTaskInvoiceData(prev => ({ ...prev, case_id: val }))}
                    options={taskClientCases.map(c => ({
                      value: c.id,
                      label: `${c.case_number} - ${c.title}`
                    }))}
                    emptyOption={!taskInvoiceData.client_id ? 'Select Client First' : 'Select Case'}
                    required
                    disabled={!taskInvoiceData.client_id}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Attorney *
                  </label>
                  <SearchableSelect
                    value={taskInvoiceData.billing_attorney_id}
                    onChange={(val) => setTaskInvoiceData(prev => ({ ...prev, billing_attorney_id: val }))}
                    options={(attorneys || []).map(attorney => ({
                      value: attorney.id,
                      label: `${attorney.first_name} ${attorney.last_name}`
                    }))}
                    emptyOption="Select Attorney"
                    required
                  />
                </div>
              </div>

              {/* Due Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={taskInvoiceData.due_date}
                    onChange={(e) => setTaskInvoiceData(prev => ({ ...prev, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={taskInvoiceData.description}
                  onChange={(e) => setTaskInvoiceData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="Invoice description..."
                />
              </div>

              {/* Unbilled Items Selection */}
              {taskInvoiceData.case_id && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Select Unbilled Items
                    {(selectedTasks.size > 0 || selectedTimeEntries.size > 0 || selectedExpenses.size > 0) && (
                      <span className="ml-2 text-sm text-gray-600">
                        ({selectedTasks.size + selectedTimeEntries.size + selectedExpenses.size} selected - Total: {invoiceService.formatCurrency(calculateTaskTotal())})
                      </span>
                    )}
                  </h3>

                  {unbilledTasks.length === 0 && unbilledTime.length === 0 && unbilledExpenses.length === 0 ? (
                    <p className="text-gray-600 text-center py-8">
                      No unbilled items found for this case
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                      {/* Unbilled Tasks */}
                      {unbilledTasks.length > 0 && (
                        <div className="px-3 pt-2 pb-1">
                          <p className="text-xs font-semibold text-gray-500 uppercase">Tasks</p>
                        </div>
                      )}
                      {(unbilledTasks || []).map(task => (
                        <div key={task.id} className="flex items-center p-3 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedTasks.has(task.id)}
                            onChange={() => toggleTaskSelection(task.id)}
                            aria-label={`Select task: ${task.title}`}
                            className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">{task.title}</p>
                              <div className="flex items-center text-sm text-gray-500">
                                <Clock className="w-4 h-4 mr-1" />
                                {task.actual_hours}h
                                <DollarSign className="w-4 h-4 ml-2 mr-1" />
                                {invoiceService.formatCurrency(task.estimated_amount)}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              Completed: {invoiceService.formatDate(task.completed_at)}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Unbilled Time Entries */}
                      {unbilledTime.length > 0 && (
                        <div className="px-3 pt-2 pb-1 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase">Time Entries</p>
                        </div>
                      )}
                      {unbilledTime.map(entry => (
                        <div key={entry.id} className="flex items-center p-3 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedTimeEntries.has(entry.id)}
                            onChange={() => toggleTimeEntrySelection(entry.id)}
                            aria-label={`Select time entry: ${entry.description}`}
                            className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">{entry.description}</p>
                              <div className="flex items-center text-sm text-gray-500">
                                <Clock className="w-4 h-4 mr-1" />
                                {entry.hours}h
                                <DollarSign className="w-4 h-4 ml-2 mr-1" />
                                {invoiceService.formatCurrency(entry.amount)}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {entry.user_name} - {invoiceService.formatDate(entry.logged_at)}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* Unbilled Expenses */}
                      {unbilledExpenses.length > 0 && (
                        <div className="px-3 pt-2 pb-1 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase">Expenses</p>
                        </div>
                      )}
                      {unbilledExpenses.map(expense => (
                        <div key={expense.id} className="flex items-center p-3 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedExpenses.has(expense.id)}
                            onChange={() => toggleExpenseSelection(expense.id)}
                            aria-label={`Select expense: ${expense.description}`}
                            className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                              <div className="flex items-center text-sm text-gray-500">
                                <Receipt className="w-4 h-4 mr-1" />
                                {expense.category || 'Expense'}
                                <DollarSign className="w-4 h-4 ml-2 mr-1" />
                                {invoiceService.formatCurrency(expense.amount)}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500">
                              {expense.user_name ? `${expense.user_name} - ` : ''}{invoiceService.formatDate(expense.date)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={activeTab === 'manual' ? handleManualSubmit : handleTaskSubmit}
            disabled={loading}
            className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-md text-sm font-medium hover:bg-yellow-600 disabled:opacity-50 flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              'Create Invoice'
            )}
          </button>
        </div>
      </div>

      {/* Rate update prompt */}
      {ratePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Update default rate?</h3>
            <p className="text-sm text-gray-600 mb-4">
              You changed the rate for {ratePrompt.attorneyName} to ${ratePrompt.newRate.toFixed(2)}/hr.
              Would you like to update their default hourly rate?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRatePrompt(null)}
                className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                No, just this invoice
              </button>
              <button
                onClick={handleUpdateDefaultRate}
                className="px-3 py-1.5 text-sm text-white bg-yellow-600 rounded-md hover:bg-yellow-700"
              >
                Yes, update default
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default CreateInvoiceModal;
